import { type Currency } from '../../../../../core/entities/external/Currency/Currency'

interface ExpenseReportItem {
  amount: string
  taxMode: 'percent' | 'dollar' | 'saved'
  taxValue: string
  reimbursable: boolean
  currency: Currency
}

interface ComputeExpenseReportTotalsInput {
  items: readonly ExpenseReportItem[]
}

interface ComputeExpenseReportTotalsOutput {
  totalAmount: number
  totalTax: number
  subtotal: number
  reimbursableTotal: number
}

export class ComputeExpenseReportTotalsUseCase {
  execute(
    input: ComputeExpenseReportTotalsInput
  ): ComputeExpenseReportTotalsOutput {
    const { items } = input

    const totalAmount = items.reduce(
      (sum, item) =>
        sum + Number.parseFloat(item.amount || '0') * item.currency.rate,
      0
    )

    const totalTax = items.reduce((sum, item) => {
      const total = Number.parseFloat(item.amount || '0')
      const taxValue = Number.parseFloat(item.taxValue || '0')

      if (item.taxMode === 'percent') {
        const rate = taxValue / 100
        if (rate <= 0) return sum
        const itemSubtotal = total / (1 + rate)
        return sum + (total - itemSubtotal) * item.currency.rate
      }
      return sum + taxValue * item.currency.rate
    }, 0)

    const subtotal = totalAmount - totalTax

    const reimbursableTotal = items
      .filter((item) => item.reimbursable)
      .reduce(
        (sum, item) =>
          sum + Number.parseFloat(item.amount || '0') * item.currency.rate,
        0
      )

    return { totalAmount, totalTax, subtotal, reimbursableTotal }
  }
}
